import { useEffect } from 'react';

import { getUTMParams } from '@eneco/common/querystring';
import { getStoredUTMParams } from '@eneco/common/querystring/utmStorage';
import { OpenAPI } from '@eneco/dc/client/OpenAPI';
import { useSalesV2CreateOrderV2 } from '@eneco/dc/hooks/smartthermostat';
import useDC from '@eneco/dc/useDC';
import { useCustomerProfileAccount } from '@eneco/hooks/profile';
import type { AddressDto } from '@monorepo-types/dc/smartthermostat';
import Cookies from 'js-cookie';

import { FlowContext } from '../types';
import { useFlowHooks } from './FlowProvider';

const SOURCE_CHANNEL_HEADER = 'X-Source-Channel';

// Shared by flows that create a smartthermostat order from the current basket; basketId identifies
// the flow's basket cookie (e.g. 'bundle-flow_basket', 'dongle-flow_basket').
export const useOrderOffer = ({ offer, basketId }: { offer?: { offerIds?: string[] }; basketId: string }) => {
  const { send, isSuccess, isError } = useSalesV2CreateOrderV2();
  const { currentAccount } = useCustomerProfileAccount();
  const { businessUnit, label } = useDC();

  const { useBasketActorRef, useBasketSelector } = useFlowHooks<FlowContext>();
  const { send: sendToBasketMachine } = useBasketActorRef();
  const referenceId = useBasketSelector(state => state?.context?.basketId) ?? '';
  const isBasketReady = useBasketSelector(state => state.matches('SUCCESS') && !!state.context?.basketId);
  const offerIds = offer?.offerIds ?? [];

  const accountAddress = currentAccount?.address;
  const parsedHouseNumber = Number(accountAddress?.houseNumber);
  const trimmedPostalCode = accountAddress?.postalCode?.trim() ?? '';
  const isAddressReady = !!accountAddress && parsedHouseNumber > 0 && trimmedPostalCode.length > 0;

  const deliveryAddress: AddressDto = {
    houseNumber: parsedHouseNumber,
    houseNumberSuffix: accountAddress?.houseNumberSuffix ?? '',
    postalCode: trimmedPostalCode,
    addressSuffix: '',
    hasResidentialPurpose: true,
  };

  useEffect(() => {
    sendToBasketMachine({ type: 'GET_BASKET', config: { businessUnit, label }, id: Cookies.get(basketId) });
  }, [basketId, businessUnit, label, sendToBasketMachine]);

  function placeOrder() {
    if (!referenceId || !isAddressReady) return;

    // Prioritizes the current URL's utm_medium over stored attribution (mirrors GenericForm's request util); defaults to 'Web' outside the app webview.
    const utmMedium = getUTMParams()?.utm_medium ?? getStoredUTMParams()?.utm_medium;
    const externalChannel = utmMedium === 'app' ? 'App' : 'Web';

    // OpenAPI.HEADERS is global and merged into every DC request, so restore it once this order request settles.
    const previousChannelHeader = OpenAPI.HEADERS[SOURCE_CHANNEL_HEADER];
    OpenAPI.HEADERS[SOURCE_CHANNEL_HEADER] = externalChannel;

    send({
      referenceId,
      offerIds,
      deliveryAddress,
    }).finally(() => {
      if (previousChannelHeader === undefined) {
        delete OpenAPI.HEADERS[SOURCE_CHANNEL_HEADER];
      } else {
        OpenAPI.HEADERS[SOURCE_CHANNEL_HEADER] = previousChannelHeader;
      }
    });
  }

  return { isSuccess, isError, isBasketReady, isAddressReady, placeOrder };
};
